import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import api from "../services/api";

const PR_CSS = `
.pr-wrap { margin-top: 40px; font-family: 'DM Sans', sans-serif; }
.pr-title { font-family: 'Playfair Display', serif; font-size: 1.5rem; font-weight: 700; color: #1a1a1a; margin-bottom: 18px; }
.pr-summary { display: flex; align-items: center; gap: 16px; padding: 18px 20px; background: #f7f5f1; border: 1px solid #e8e4df; border-radius: 14px; margin-bottom: 22px; }
.pr-avg { font-family: 'Playfair Display', serif; font-size: 2.4rem; font-weight: 700; color: #1a5c38; line-height: 1; }
.pr-count { font-size: 0.85rem; color: #6a6a6a; margin-top: 4px; }
.pr-stars { display: inline-flex; gap: 2px; }
.pr-star { line-height: 1; }
.pr-form { background: #fff; border: 1px solid #e8e4df; border-radius: 14px; padding: 20px; margin-bottom: 24px; }
.pr-form-title { font-weight: 700; color: #1a1a1a; margin-bottom: 14px; }
.pr-field { margin-bottom: 14px; }
.pr-field label { display: block; font-size: 0.82rem; font-weight: 600; color: #4a4a4a; margin-bottom: 6px; }
.pr-field input, .pr-field textarea { width: 100%; border: 1px solid #d8d3cc; border-radius: 10px; padding: 10px 12px; font-family: 'DM Sans', sans-serif; font-size: 0.9rem; box-sizing: border-box; }
.pr-field input:focus, .pr-field textarea:focus { outline: none; border-color: #1a5c38; }
.pr-submit { background: #1a5c38; color: #fff; border: none; border-radius: 100px; padding: 11px 26px; font-weight: 600; cursor: pointer; }
.pr-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.pr-note { background: #f0f9f4; border: 1px solid #c5dece; color: #1a5c38; border-radius: 12px; padding: 14px 16px; font-size: 0.88rem; margin-bottom: 24px; }
.pr-list { display: flex; flex-direction: column; gap: 16px; }
.pr-item { border-bottom: 1px solid #eee; padding-bottom: 16px; }
.pr-item-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 6px; }
.pr-name { font-weight: 700; color: #1a1a1a; font-size: 0.9rem; }
.pr-verified { font-size: 0.72rem; font-weight: 700; color: #1a5c38; background: #e8f3ed; border-radius: 100px; padding: 2px 8px; }
.pr-date { font-size: 0.78rem; color: #9a9a9a; margin-left: auto; }
.pr-item-title { font-weight: 600; color: #1a1a1a; margin-top: 4px; }
.pr-item-comment { color: #4a4a4a; font-size: 0.9rem; line-height: 1.6; margin-top: 4px; white-space: pre-wrap; }
.pr-empty { color: #8a8a8a; font-size: 0.9rem; }
`;

const Stars = ({ value, onChange, size = 18 }) => (
  <span className="pr-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <span
        key={n}
        className="pr-star"
        onClick={onChange ? () => onChange(n) : undefined}
        role={onChange ? "button" : undefined}
        style={{ cursor: onChange ? "pointer" : "default", color: n <= value ? "#f4b942" : "#d8d8d8", fontSize: size }}
      >★</span>
    ))}
  </span>
);

export default function ProductReviews({ productId }) {
  const { t } = useTranslation();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [rating, setRating]       = useState(0);
  const [title, setTitle]         = useState("");
  const [comment, setComment]     = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/reviews/product/${productId}`)
      .then((res) => {
        setData(res.data);
        if (res.data.myReview) {
          setRating(res.data.myReview.rating || 0);
          setTitle(res.data.myReview.title || "");
          setComment(res.data.myReview.comment || "");
        }
      })
      .catch(() => setData({ enabled: true, summary: { count: 0, average: 0 }, reviews: [], canReview: false, myReview: null }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (productId) load(); /* eslint-disable-next-line */ }, [productId]);

  if (loading || !data || data.enabled === false) return null;

  const submit = async () => {
    if (!(rating >= 1)) { toast.warning(t("reviews.pickRating")); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { product_id: productId, rating, title, comment });
      toast.success(data.myReview ? t("reviews.updated") : t("reviews.thanks"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || t("reviews.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const isLoggedIn = localStorage.getItem("authToken") && localStorage.getItem("isGuest") !== "true";
  const { summary, reviews, canReview, myReview } = data;

  return (
    <div className="pr-wrap">
      <style>{PR_CSS}</style>
      <h2 className="pr-title">{t("reviews.title")}</h2>

      <div className="pr-summary">
        <div className="pr-avg">{(summary.average || 0).toFixed(1)}</div>
        <div>
          <Stars value={Math.round(summary.average || 0)} />
          <div className="pr-count">{t("reviews.count", { count: summary.count })}</div>
        </div>
      </div>

      {isLoggedIn && canReview ? (
        <div className="pr-form">
          <div className="pr-form-title">{myReview ? t("reviews.updateTitle") : t("reviews.writeTitle")}</div>
          <div className="pr-field">
            <label>{t("reviews.yourRating")}</label>
            <Stars value={rating} onChange={setRating} size={28} />
          </div>
          <div className="pr-field">
            <label>{t("reviews.titleLabel")}</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder={t("reviews.titlePlaceholder")} />
          </div>
          <div className="pr-field">
            <label>{t("reviews.reviewLabel")}</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000} rows={4} placeholder={t("reviews.reviewPlaceholder")} />
          </div>
          <button className="pr-submit" disabled={submitting} onClick={submit}>
            {submitting ? t("reviews.submitting") : myReview ? t("reviews.updateBtn") : t("reviews.submitBtn")}
          </button>
        </div>
      ) : isLoggedIn ? (
        <div className="pr-note">{t("reviews.purchasersOnly")}</div>
      ) : (
        <div className="pr-note">{t("reviews.loginToReview")}</div>
      )}

      <div className="pr-list">
        {reviews.length === 0 ? (
          <div className="pr-empty">{t("reviews.empty")}</div>
        ) : (
          reviews.map((r) => (
            <div className="pr-item" key={r._id}>
              <div className="pr-item-head">
                <span className="pr-name">{`${r.user_id?.firstName || ""} ${(r.user_id?.lastName || "").charAt(0)}`.trim() || t("reviews.customer")}</span>
                {r.verified_purchase && <span className="pr-verified">✓ {t("reviews.verified")}</span>}
                <span className="pr-date">{new Date(r.createdAt).toLocaleDateString()}</span>
              </div>
              <Stars value={r.rating} size={15} />
              {r.title && <div className="pr-item-title">{r.title}</div>}
              {r.comment && <div className="pr-item-comment">{r.comment}</div>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
